import { Group } from '@wellesley/openapi'
import { ItemWrapper } from '../../ItemWrapper'
import { GroupJoinButton, isPrivateGroup } from '@wellesley/group'
import { Avatar, AvatarFallback, AvatarImage } from '@wellesley/ui'
import { Globe, LockKeyhole } from 'lucide-react'
import { authSlice } from '@wellesley/auth'

import { useSelector } from 'react-redux'

type GroupResultProps = {
  group: Group
  onItemClick?: (item: Group) => void
  searchQuery?: string
}

const getGroupImage = (group: Group) => {
  try {
    return group.avatar?.files?.[0]?.uri || ''
  } catch (e) {
    return ''
  }
}

export const GroupResult = ({
  group,
  onItemClick,
  searchQuery,
}: GroupResultProps) => {
  const isLogged = useSelector(authSlice.selectors.selectIsAuthenticated)

  const handleClick = () => {
    onItemClick?.(group)
  }

  const getGroupTypeText = () => {
    if (isPrivateGroup(group)) {
      return (
        <span className="flex items-center gap-xxxs">
          <LockKeyhole size={16} className="text-gray-500" /> Private group
        </span>
      )
    }
    return (
      <span className="flex items-center gap-xxxs">
        <Globe size={16} className="text-gray-500" /> Public group
      </span>
    )
  }

  const getMemberCount = () => {
    const memberCount = group.stats?.members ?? 0
    if (memberCount >= 1000) {
      return `${(memberCount / 1000).toFixed(1)}K`
    }
    return memberCount.toString()
  }

  const getMemberText = () => {
    const count = getMemberCount()
    return `· ${count} members`
  }

  return (
    <ItemWrapper
      url="/g/:groupname"
      props={{ groupname: group.groupName }}
      onClick={handleClick}
      searchQuery={searchQuery}
      className="max-w-full"
      dataTestId={`group-result-${group.groupName}`}
    >
      <Avatar className="size-10 rounded-full">
        {getGroupImage(group) && (
          <AvatarImage src={getGroupImage(group)} alt="group-avatar" />
        )}
        <AvatarFallback className="truncate bg-gray-700 text-white">
          {group.displayName?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex min-w-0 flex-1 flex-col items-start">
        <div className="flex w-full items-center gap-xxxs">
          <span className="truncate text-base font-semibold text-gray-900">
            {group.displayName}
          </span>
        </div>
        <span className="flex w-full items-center gap-xxxs truncate text-sm font-medium text-gray-500">
          {getGroupTypeText()} <span>{getMemberText()}</span>
        </span>
      </div>

      {isLogged && (
        <div
          className="ml-auto"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
          }}
        >
          <GroupJoinButton group={group} onVisit={handleClick} />
        </div>
      )}
    </ItemWrapper>
  )
}
